import { parseTime } from './time';

export interface ParsedMoment {
  /** Stable id from the line the time sits on: "l12". */
  id: string;
  line: number;
  /** 1, 2, 3 (extra time 1), 4 (extra time 2); 0 when the list is in video time. */
  period: number;
  /** Match clock or video time, in seconds, depending on the list's time base. */
  timeSec: number;
  /** Null means "use the default clip length". */
  durationSec: number | null;
  lengthSource: 'given' | 'range' | 'default';
  label: string;
}

export interface ParseIssue {
  line: number;
  text: string;
  reason: string;
}

export interface ParseResult {
  moments: ParsedMoment[];
  /** Lines that look like they should hold a moment but don't. Shown as warnings. */
  skipped: ParseIssue[];
  /** Lines with no timestamp that are safely ignored (names, labels). Veo lists only. */
  ignored: ParseIssue[];
  duplicates: number;
  periodCounts: Record<number, number>;
}

const HEADING_RE = /(?:(\d)\s*(?:st|nd|rd|th)?|(first|second|third|fourth))\s*(?:period|half)\b|\b(?:period|half)\s*(\d)\b/i;
const WORD_PERIOD: Record<string, number> = { first: 1, second: 2, third: 3, fourth: 4 };

function headingPeriod(m: RegExpMatchArray): number {
  if (m[1]) return Number(m[1]);
  if (m[2]) return WORD_PERIOD[m[2].toLowerCase()];
  return Number(m[3]);
}

function lineStarts(text: string): number[] {
  const starts = [0];
  for (let i = 0; i < text.length; i++) if (text[i] === '\n') starts.push(i + 1);
  return starts;
}

function lineAt(starts: number[], index: number): number {
  let lo = 0;
  let hi = starts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (starts[mid] <= index) lo = mid;
    else hi = mid - 1;
  }
  return lo + 1;
}

/**
 * Veo's copied highlight list: "1st period" headings and moments like "02:5914s" (starts at 02:59, lasts 14 s).
 * Same rules as make-timestamps.mjs: time and length may be split by spaces or line breaks, any other text is
 * ignored, duplicates are removed.
 */
export function parseVeo(text: string): ParseResult {
  const re = /(\d+)\s*(?:st|nd|rd|th)\s+(?:period|half)|(\d{1,3}):(\d{2})\s*(\d{1,3})\s*s(?![a-z])/gi;
  const starts = lineStarts(text);
  const lines = text.split('\n');
  const consumed = new Set<number>();
  const seen = new Set<string>();
  const moments: ParsedMoment[] = [];
  const periodCounts: Record<number, number> = {};
  let duplicates = 0;
  let period = 1;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const first = lineAt(starts, m.index);
    const last = lineAt(starts, m.index + m[0].length - 1);
    for (let l = first; l <= last; l++) consumed.add(l);
    if (m[1]) {
      period = Number(m[1]);
      continue;
    }
    const ss = Number(m[3]);
    if (ss > 59) continue;
    const key = `${period}|${m[2]}:${m[3]}|${Number(m[4])}`;
    if (seen.has(key)) {
      duplicates++;
      continue;
    }
    seen.add(key);
    moments.push({
      id: `l${first}`,
      line: first,
      period,
      timeSec: Number(m[2]) * 60 + ss,
      durationSec: Number(m[4]),
      lengthSource: 'given',
      label: '',
    });
    periodCounts[period] = (periodCounts[period] ?? 0) + 1;
  }
  const ignored: ParseIssue[] = [];
  const skipped: ParseIssue[] = [];
  lines.forEach((raw, i) => {
    const line = i + 1;
    const t = raw.trim();
    if (!t || consumed.has(line)) return;
    // A time with no length is a likely mistake; anything else is a label or a name.
    if (/\d{1,3}:\d{2}/.test(t)) skipped.push({ line, text: t, reason: 'Time without a length (expected like 02:5914s)' });
    else ignored.push({ line, text: t, reason: 'No timestamp, ignored' });
  });
  return { moments, skipped, ignored, duplicates, periodCounts };
}

const TIME_TOKEN = /(\d{1,2}:\d{2}:\d{2}|\d{1,3}:\d{2}|\d{1,3}\s*\+\s*\d{1,2}'|\d{1,3}')/;

function tokenSeconds(token: string): number | null {
  const plus = token.match(/^(\d{1,3})\s*\+\s*(\d{1,2})'$/);
  if (plus) return (Number(plus[1]) + Number(plus[2])) * 60;
  const min = token.match(/^(\d{1,3})'$/);
  if (min) return Number(min[1]) * 60;
  return parseTime(token);
}

function cleanLabel(s: string): string {
  return s.replace(/^[\s\-–—:·|,.;]+|[\s\-–—:·|,.;]+$/g, '').replace(/\s+/g, ' ');
}

/**
 * Any list of times, one moment per line:
 *   "23:41", "23:41 20s Goal", "31:18-31:40 Save", "45+2' corner", "1:02:15", "02:5914s".
 * Lines like "2nd half" switch the period. In video-time mode periods are ignored.
 */
export function parseList(text: string, timeBase: 'match' | 'video'): ParseResult {
  const moments: ParsedMoment[] = [];
  const skipped: ParseIssue[] = [];
  const periodCounts: Record<number, number> = {};
  let period = 1;
  text.split('\n').forEach((raw, i) => {
    const line = i + 1;
    const t = raw.trim();
    if (!t) return;
    const heading = t.match(HEADING_RE);
    const timeMatch = t.match(TIME_TOKEN);
    if (heading && (!timeMatch || (heading.index ?? 0) < (timeMatch.index ?? 0))) {
      if (!timeMatch) {
        period = headingPeriod(heading);
        return;
      }
      period = headingPeriod(heading);
    }
    if (/^extra[\s-]*time\b/i.test(t) && !timeMatch) {
      period = 3;
      return;
    }
    if (!timeMatch) {
      skipped.push({ line, text: t, reason: 'No exact time found' });
      return;
    }
    const before = t.slice(0, timeMatch.index);
    let rest = t.slice((timeMatch.index ?? 0) + timeMatch[0].length);
    let start = tokenSeconds(timeMatch[1]);
    let duration: number | null = null;
    let lengthSource: ParsedMoment['lengthSource'] = 'default';
    // Veo style glued length: "02:5914s"
    const glued = t.slice(timeMatch.index).match(/^(\d{1,3}):(\d{2})(\d{1,3})\s*s(?![a-z])/i);
    if (glued) {
      start = Number(glued[1]) * 60 + Number(glued[2]);
      duration = Number(glued[3]);
      lengthSource = 'given';
      rest = t.slice((timeMatch.index ?? 0) + glued[0].length);
    }
    if (start === null) {
      skipped.push({ line, text: t, reason: 'Time could not be read' });
      return;
    }
    if (duration === null) {
      const range = rest.match(/^\s*(?:-|–|—|to)\s*(\d{1,2}:\d{2}:\d{2}|\d{1,3}:\d{2})/i);
      const len = rest.match(/^\s*\+?\s*(\d{1,3})\s*(?:s|sec|secs|seconds)(?![a-z])/i);
      if (range) {
        const end = parseTime(range[1]);
        if (end !== null && end > start) {
          duration = end - start;
          lengthSource = 'range';
        }
        rest = rest.slice(range[0].length);
      } else if (len) {
        duration = Number(len[1]);
        lengthSource = 'given';
        rest = rest.slice(len[0].length);
      }
    }
    const p = timeBase === 'video' ? 0 : period;
    moments.push({
      id: `l${line}`,
      line,
      period: p,
      timeSec: start,
      durationSec: duration,
      lengthSource,
      label: cleanLabel(`${heading ? '' : before} ${rest}`),
    });
    periodCounts[p] = (periodCounts[p] ?? 0) + 1;
  });
  return { moments, skipped, ignored: [], duplicates: 0, periodCounts };
}

/** True when the text reads like a pasted Veo list. */
export function looksLikeVeo(text: string): boolean {
  return /\d{1,3}:\d{2}\s*\d{1,3}\s*s(?![a-z])/i.test(text) && /(?:st|nd)\s+period/i.test(text);
}
